import type { CpAccordionItem } from "../../components/CpAccordion/CpAccordion";

export const STAGES = ["Build", "Grow", "Lead", "Scale"];

export interface ReportRow {
  name: string;
  desc: string;
  /** First stage index where the report is available (stages are cumulative). */
  min: number;
}

export interface ReportGroup {
  key: string;
  icon: string;
  title: string;
  question: string;
  body: string;
  rows: ReportRow[];
}

export const GROUPS: ReportGroup[] = [
  {
    key: "revenue",
    icon: "trending-up",
    title: "Sales & Revenue",
    question: "How is my revenue trending?",
    body: "Invoice trends, aging revenue and time-series sales so you can spot busy times and plan for slower months.",
    rows: [
      {
        name: "Average Invoice Comparison",
        desc: "See how your average invoice has trended over different periods.",
        min: 0,
      },
      {
        name: "Revenue by Invoice Age",
        desc: "Make financial decisions from an aging view of outstanding revenue.",
        min: 0,
      },
      {
        name: "Total Sales by Year & Month",
        desc: "Spot busy times and shoulder months with a time-series revenue view.",
        min: 0,
      },
    ],
  },
  {
    key: "customers",
    icon: "users",
    title: "Customers",
    question: "Which customers drive my business?",
    body: "New versus existing revenue, replacement efficiency and lifetime value by customer tier.",
    rows: [
      {
        name: "Total Sales – New vs Existing Customers",
        desc: "Focus marketing by revenue split between new and existing customers.",
        min: 0,
      },
      {
        name: "Existing Customer Replacement Efficiency",
        desc: "See how often existing-customer calls turn into replacement sales.",
        min: 2,
      },
      {
        name: "New Customer Replacement Efficiency",
        desc: "See how often new-customer calls turn into replacement sales.",
        min: 2,
      },
      {
        name: "Lifetime Value by Tier",
        desc: "See the long-term lifetime value of each tier of your customers.",
        min: 3,
      },
    ],
  },
  {
    key: "market",
    icon: "map",
    title: "Market & ZIP Code",
    question: "Where is my market?",
    body: "Sales, new customers and households by ZIP code — plus the total addressable market near you.",
    rows: [
      {
        name: "Percentage of New Customers by ZIP",
        desc: "See where your new customers come from to focus your marketing.",
        min: 1,
      },
      {
        name: "Total Households by ZIP",
        desc: "Spot the true potential in your market and respond accordingly.",
        min: 1,
      },
      {
        name: "Total Sales by ZIP Code",
        desc: "See which ZIP codes drive results with total sales by ZIP code.",
        min: 1,
      },
      {
        name: "Total Sales from New Customers by ZIP & Year",
        desc: "See where new customer sales come from to guide service and marketing.",
        min: 1,
      },
      {
        name: "Total Addressable Market",
        desc: "See your business potential through the addressable market near you.",
        min: 3,
      },
    ],
  },
];

export const FAQS: CpAccordionItem[] = [
  {
    title: "Which reports come with my membership?",
    body: "Every stage includes Insights Reports. Build starts with 4 core revenue and customer reports, Grow adds ZIP code and market reports, Lead adds replacement efficiency, and Scale unlocks all 12.",
  },
  {
    title: "Where does the report data come from?",
    body: "Insights Reports are built from your own business data — invoices, customers and sales — alongside market data for your area.",
  },
  {
    title: "Who are Insights Reports for?",
    body: "Owners in every trade — HVAC, Plumbing, Electrical and Roofing — who want to make decisions from their numbers.",
  },
  {
    title: "How do I get more reports?",
    body: "Reports unlock as you move up membership stages. Talk to your CertainPath coach about the stage that fits your business.",
  },
];
